import { useEffect } from "react";
import { Star, ShieldCheck, Sparkles, FlaskConical, Loader2 } from "lucide-react";
import { BadgeExplainer } from "./BadgeExplainer";

export function useSeo(title, desc, jsonLd) {
  useEffect(() => {
    document.title = title ? `${title} | MyHomelyHub` : "MyHomelyHub";
    const set = (sel, attr, val) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement(sel.startsWith("link") ? "link" : "meta");
        if (sel.includes("name=")) el.setAttribute("name", "description");
        if (sel.startsWith("link")) el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
      }
      el.setAttribute(attr, val);
    };
    if (desc) set('meta[name="description"]', "content", desc);
    set('link[rel="canonical"]', "href", window.location.origin + window.location.pathname);
    let s = document.getElementById("ld-json");
    if (jsonLd) {
      if (!s) {
        s = document.createElement("script");
        s.type = "application/ld+json";
        s.id = "ld-json";
        document.head.appendChild(s);
      }
      s.textContent = JSON.stringify(jsonLd);
    } else if (s) s.remove();
  }, [title, desc, jsonLd && JSON.stringify(jsonLd)]); // eslint-disable-line
}

export const Stars = ({ value = 0, count, size = 14 }) => (
  <span className="inline-flex items-center gap-1 text-sm" aria-label={`Rated ${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
    ))}
    <span className="font-semibold text-slate-800 ml-0.5">{Number(value || 0).toFixed(1)}</span>
    {count !== undefined && <span className="text-slate-500">({count})</span>}
  </span>
);

export const VerifiedBadge = ({ levels, details }) =>
  levels?.includes("Enhanced Verified") ? (
    <BadgeExplainer levels={levels} details={details}><span className="chip bg-amber-50 text-amber-800 border border-amber-300" data-testid="verified-badge"><ShieldCheck size={12} /> Enhanced Verified</span></BadgeExplainer>
  ) : levels?.length ? (
    <BadgeExplainer levels={levels} details={details}><span className="chip bg-sky-50 text-sky-700 border border-sky-200" data-testid="verified-badge">
      <ShieldCheck size={12} /> {levels.length > 1 ? `${levels.length} checks verified` : levels[0]}
    </span></BadgeExplainer>
  ) : null;

export const FeaturedBadge = ({ label = "Featured" }) => (
  <span className="chip bg-amber-50 text-amber-700 border border-amber-200"><Sparkles size={12} /> {label}</span>
);

export const DemoBadge = () => (
  <span className="chip bg-violet-50 text-violet-700 border border-violet-200" title="Sample data for demonstration — not a real business" data-testid="demo-badge">
    <FlaskConical size={12} /> Demo data
  </span>
);

const STATUS = {
  draft: "bg-slate-100 text-slate-600", published: "bg-sky-50 text-sky-700", receiving_applications: "bg-sky-50 text-sky-700",
  shortlisted: "bg-indigo-50 text-indigo-700", provider_selected: "bg-emerald-50 text-emerald-700", scheduled: "bg-teal-50 text-teal-700",
  in_progress: "bg-amber-50 text-amber-700", completed: "bg-emerald-100 text-emerald-800", cancelled: "bg-rose-50 text-rose-700",
  disputed: "bg-rose-100 text-rose-800", approved: "bg-emerald-50 text-emerald-700", pending: "bg-amber-50 text-amber-700",
  rejected: "bg-rose-50 text-rose-700", suspended: "bg-rose-100 text-rose-800", active: "bg-emerald-50 text-emerald-700",
  applied: "bg-sky-50 text-sky-700", selected: "bg-emerald-100 text-emerald-800", not_selected: "bg-slate-100 text-slate-500",
  declined: "bg-rose-50 text-rose-700", contacted: "bg-violet-50 text-violet-700", paid: "bg-emerald-50 text-emerald-700",
  refunded: "bg-rose-50 text-rose-700", open: "bg-amber-50 text-amber-700", won: "bg-emerald-100 text-emerald-800", lost: "bg-slate-100 text-slate-500",
};

export const StatusBadge = ({ status }) => (
  <span className={`chip capitalize ${STATUS[status] || "bg-slate-100 text-slate-600"}`} data-testid={`status-${status}`}>
    {String(status || "").replace(/_/g, " ")}
  </span>
);

export const EmptyState = ({ icon: Icon, title, text, action, testid = "empty-state" }) => (
  <div className="card p-10 text-center fade-up" data-testid={testid}>
    {Icon && <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-[#FFF1EE] text-[#FF8166] grid place-items-center"><Icon size={26} /></div>}
    <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
    {text && <p className="muted mt-1 max-w-md mx-auto text-sm">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const StatCard = ({ label, value, icon: Icon, hint, testid, tone = "navy" }) => (
  <div className="card p-5 fade-up" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span>
      {Icon && <span className={`w-9 h-9 rounded-xl grid place-items-center ${tone === "coral" ? "bg-[#FFF1EE] text-[#FF8166]" : "bg-[#EBF0F4] text-[#374B5C]"}`}><Icon size={18} /></span>}
    </div>
    <div className="mt-3 text-2xl font-bold font-display text-slate-900">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

export const PageTitle = ({ title, subtitle, actions }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{title}</h1>
      {subtitle && <p className="muted mt-1 text-sm">{subtitle}</p>}
    </div>
    {actions && <div className="flex gap-2 flex-wrap">{actions}</div>}
  </div>
);

export const Loader = ({ label = "Loading" }) => (
  <div className="flex items-center justify-center gap-2 py-16 text-slate-500" role="status"><Loader2 className="animate-spin" size={18} /> {label}…</div>
);

export const Skeleton = ({ className = "h-24" }) => <div className={`animate-pulse rounded-2xl bg-slate-100 ${className}`} />;

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="label">{label}</span>
    {children}
    {hint && <span className="text-xs text-slate-500 mt-1 block">{hint}</span>}
  </label>
);
